import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventNoteIcon from "@mui/icons-material/EventNoteOutlined";
import ContactPhoneIcon from "@mui/icons-material/ContactPhoneOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalanceOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import StorefrontIcon from "@mui/icons-material/StorefrontOutlined";
import StarIcon from "@mui/icons-material/StarOutlineOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LockResetIcon from "@mui/icons-material/LockResetOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { doc, onSnapshot } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, db } from "../lib/firebase";
import { useSession } from "../auth/AuthProvider";
import { formatJalali } from "../lib/format";
import { ChangePasswordDialog } from "../components/ChangePasswordDialog";

const DRAWER_WIDTH = 252;

const NAV_GROUPS: {
  caption: string;
  items: { to: string; label: string; icon: JSX.Element }[];
}[] = [
  {
    caption: "عملیات",
    items: [
      { to: "/", label: "داشبورد", icon: <DashboardIcon /> },
      { to: "/calendar", label: "تقویم رزرو", icon: <CalendarMonthIcon /> },
      { to: "/bookings", label: "رزروها", icon: <EventNoteIcon /> },
      { to: "/contracts", label: "قراردادها", icon: <DescriptionIcon /> },
      { to: "/crm", label: "مشتریان", icon: <ContactPhoneIcon /> },
    ],
  },
  {
    caption: "مالی و اداری",
    items: [
      { to: "/finance", label: "مالی", icon: <AccountBalanceIcon /> },
      { to: "/payouts", label: "تسویه‌ها", icon: <PaymentsIcon /> },
      { to: "/hr", label: "کارمندان", icon: <GroupsIcon /> },
    ],
  },
  {
    caption: "بازار",
    items: [
      { to: "/halls", label: "تالارها", icon: <StorefrontIcon /> },
      { to: "/reviews", label: "نظرات", icon: <StarIcon /> },
    ],
  },
];

export function DashboardLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop sidebar collapse state, remembered across sessions.
  const [desktopOpen, setDesktopOpen] = useState(
    () => localStorage.getItem("talar.sidebar") !== "collapsed",
  );
  const [passwordOpen, setPasswordOpen] = useState(false);
  const { role, orgId, user } = useSession();
  const [orgName, setOrgName] = useState<string>("");
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  function toggleSidebar() {
    if (isDesktop) {
      setDesktopOpen((v) => {
        localStorage.setItem("talar.sidebar", v ? "collapsed" : "open");
        return !v;
      });
    } else {
      setMobileOpen((v) => !v);
    }
  }

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(doc(db, "organizations", orgId), (snap) => {
      setOrgName((snap.data()?.name as string) ?? "");
    });
  }, [orgId]);

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h5" color="primary">
          تالار
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {orgName || "پنل مدیریت"}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        {NAV_GROUPS.map((group) => (
          <Box key={group.caption} mb={0.5}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ px: 3, fontSize: 10.5 }}
            >
              {group.caption}
            </Typography>
            <List dense disablePadding>
              {group.items.map((item) => (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}
        {role === "admin" && (
          <>
            <Typography variant="overline" color="text.secondary" sx={{ px: 3, fontSize: 10.5 }}>
              پلتفرم
            </Typography>
            <List dense disablePadding>
              <ListItemButton component={NavLink} to="/admin">
                <ListItemIcon>
                  <AdminPanelSettingsIcon />
                </ListItemIcon>
                <ListItemText primary="مدیریت پلتفرم" />
              </ListItemButton>
            </List>
          </>
        )}
      </Box>
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <ListItemButton
          onClick={() => {
            setMobileOpen(false);
            setPasswordOpen(true);
          }}
        >
          <ListItemIcon>
            <LockResetIcon />
          </ListItemIcon>
          <ListItemText primary="تغییر رمز" />
        </ListItemButton>
        <ListItemButton
          onClick={async () => {
            await signOut(auth);
            navigate("/login");
          }}
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="خروج"
            secondary={user?.email ?? ""}
            secondaryTypographyProps={{ fontSize: 11, dir: "ltr", textAlign: "right" }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  // How much room the permanent sidebar occupies right now (0 when collapsed).
  const sidebarSpace = isDesktop && desktopOpen ? DRAWER_WIDTH : 0;

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          width: `calc(100% - ${sidebarSpace}px)`,
          mr: `${sidebarSpace}px`,
          transition: theme.transitions.create(["width", "margin"], {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 52 }}>
          <IconButton edge="start" onClick={toggleSidebar} sx={{ ml: 1 }} aria-label="منو">
            <MenuIcon />
          </IconButton>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {orgName}
            </Typography>
          </Stack>
          <Chip size="small" variant="outlined" label={`امروز: ${formatJalali(today)}`} />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { md: "none" } }}
        PaperProps={{ sx: { width: DRAWER_WIDTH } }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="persistent"
        anchor="right"
        open={isDesktop && desktopOpen}
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, borderLeft: 1, borderColor: "divider" },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Without this, a wide child (a table) refuses to shrink and
          // overflows the flex row — in RTL that clips the start of headers.
          minWidth: 0,
          overflowX: "hidden",
          px: { xs: 2, md: 4 },
          py: 3,
          mt: 6.5,
          minHeight: "100vh",
          bgcolor: "background.default",
          mr: `${sidebarSpace}px`,
          transition: theme.transitions.create("margin", {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Box sx={{ maxWidth: 1240, mx: "auto" }}>
          <Outlet />
        </Box>
      </Box>
      <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </Box>
  );
}
